import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  calcColumnMinWidth,
  CanvasElement,
  ColumnResizeData,
  ELEMENT_SLOT_CONTAINER_ATTR,
  getHTMLElementForColumn,
  getHTMLElementForNode,
  getHTMLElementsForContainerColumns,
  SlotId,
  SlotIdOf,
} from '@blaze-runtime/engine-domain';
import {
  isNextElementInDifferentLine,
  lastItemInArray,
  resizeRxUtils,
} from '@blaze-shared/shared-util';

import { isVariantOfContainer } from '../canvas-elements/element-model/element-type-guards';

@Injectable({
  providedIn: 'root',
})
export class ColumnSizeService {
  observeColumnSize(
    container: CanvasElement,
    slotId: SlotId,
  ): Observable<{ width: number; widthPercent: number }> {
    const containerHtml = getHTMLElementForNode(container.id);
    const slotHtml = getHTMLElementForColumn(container.id, slotId);

    if (!containerHtml || !slotHtml) {
      throw new Error(`Could not find slot in DOM of container: ${container.id}, slot: ${slotId}`);
    }

    const containerWidth = containerHtml.getBoundingClientRect().width;
    return resizeRxUtils.fromResizeObserver(slotHtml).pipe(
      map((entries) => {
        const entry = lastItemInArray(entries);
        const width = entry?.borderBoxSize[0].inlineSize ?? 0;
        return {
          width,
          widthPercent: (width / containerWidth) * 100,
        };
      }),
    );
  }

  /**
   * Provides width, minimum width, maximum width for every column in container and splits them into lines.
   * In general container should be in one line but it can break into multiple lines when in RWD mode.
   * @param container
   */
  calculateResizeDataForContainerColumns(
    container: CanvasElement | undefined,
  ): ColumnResizeData[][] {
    if (!isVariantOfContainer(container)) {
      throw new Error('Only columns of container element are supported!');
    }

    const containerHtml = getHTMLElementForNode(container.id);
    const slotsHtml = getHTMLElementsForContainerColumns(container.id);

    if (!containerHtml || !slotsHtml) {
      throw new Error(`Could not find slots in DOM of container: ${container.id}`);
    }

    const containerWidth = containerHtml.getBoundingClientRect().width;
    const lines: ColumnResizeData[][] = [[]];

    let prevSlotHtml: HTMLElement | undefined = undefined;
    let lineIndex = 0;
    container.slots.ids.forEach((slotId) => {
      let width = 0;
      let widthPercent = 0;
      let minWidth = 0;
      let minWidthPercent = 0;

      const slotHtml = slotsHtml.find(
        (slot) => slot.getAttribute(ELEMENT_SLOT_CONTAINER_ATTR) === slotId,
      );
      if (!slotHtml) {
        throw new Error(
          `Could not find slot in DOM of container: ${container.id}, slot: ${slotId}`,
        );
      }

      width = slotHtml.getBoundingClientRect().width;
      widthPercent = (width / containerWidth) * 100;
      minWidth = calcColumnMinWidth(slotHtml);
      minWidthPercent = (minWidth / containerWidth) * 100;

      const currColumnResizeData: ColumnResizeData = {
        slotId,
        width,
        widthPercent,
        minWidth,
        minWidthPercent,
        maxWidth: 0,
        maxWidthPercent: 0,
      };

      if (prevSlotHtml && isNextElementInDifferentLine(prevSlotHtml, slotHtml)) {
        lines.push([currColumnResizeData]);
        lineIndex += 1;
      } else {
        lines[lineIndex].push(currColumnResizeData);
      }
      prevSlotHtml = slotHtml;
    });

    lines.forEach((line) => {
      line.forEach((crd) => {
        crd.maxWidth = line.reduce(
          (acc, rd) => (crd.slotId === rd.slotId ? acc + rd.width : acc + (rd.width - rd.minWidth)),
          0,
        );
        crd.maxWidthPercent = (crd.maxWidth / containerWidth) * 100;
      });
    });

    return lines;
  }

  /**
   * Finds line to which slot belongs to.
   * @param lines
   * @param slotId
   */
  findLineResizeDataForSlotId(
    lines: ColumnResizeData[][],
    slotId: SlotIdOf<'container'>,
  ): ColumnResizeData[] | undefined {
    for (const line of lines) {
      for (const crd of line) {
        if (crd.slotId === slotId) {
          return line;
        }
      }
    }
    return undefined;
  }
}
